const mainPath = "/";

const dealsDetailTabs = ["properties", "description"] as const;
export type TDealsDetailTab = (typeof dealsDetailTabs)[number];
export const dealsDetailTabsValidator = (tab: string): tab is TDealsDetailTab =>
    dealsDetailTabs.includes(tab as TDealsDetailTab);

export type TDealsOverviewTab = "documents" | "operating-statement";
export type TDashboardTab =
    | "deal"
    | "file"
    | "extraction"
    | "user"
    | "mapping"
    | "preference"
    | "document-engine";
export type TChartsOfAccountsTab = "list" | "admin";
export type TChartOfAccountsEditTab = "details" | "definition";

const dealsOverviewTabs = [
    "documents",
    "operating-statement",
] satisfies TDealsOverviewTab[];

const dashboardTabs = [
    "deal",
    "file",
    "extraction",
    "user",
    "mapping",
    "preference",
    "document-engine",
] satisfies TDashboardTab[];

const chartsOfAccountsTabs = ["list", "admin"] satisfies TChartsOfAccountsTab[];

const chartOfAccountsEditTabs = [
    "details",
    "definition",
] satisfies TChartOfAccountsEditTab[];

export const dealsOverviewTabsValidator = (
    tab: string,
): tab is TDealsOverviewTab =>
    dealsOverviewTabs.includes(tab as TDealsOverviewTab);

export const dashboardTabsValidator = (tab: string): tab is TDashboardTab =>
    dashboardTabs.includes(tab as TDashboardTab);

export const chartsOfAccountsTabsValidator = (
    tab: string,
): tab is TChartsOfAccountsTab =>
    chartsOfAccountsTabs.includes(tab as TChartsOfAccountsTab);

export const chartOfAccountsEditTabsValidator = (
    tab: string,
): tab is TChartOfAccountsEditTab =>
    chartOfAccountsEditTabs.includes(tab as TChartOfAccountsEditTab);

export const routerPaths = {
    main: {
        default: () => mainPath,
    },
    auth: {
        loginCallback: () => "/login/callback",
    },
    extraction: {
        mappings: ({
            projectId,
            databaseId,
            extractionId,
        }: {
            projectId: string;
            databaseId: string;
            extractionId: string;
        }) =>
            `/databases/${databaseId}/projects/${projectId}/extraction/${extractionId}`,
        review: ({
            projectId,
            databaseId,
            extractionId,
        }: {
            projectId: string;
            databaseId: string;
            extractionId: string;
        }) =>
            `/databases/${databaseId}/projects/${projectId}/extraction/${extractionId}/review`,
    },
    projects: {
        create: ({ databaseId }: { databaseId: string }) =>
            `/databases/${databaseId}/projects/create`,
        read: ({
            databaseId,
            projectId,
            tab,
        }: {
            databaseId: string;
            projectId: string;
            tab: TDealsOverviewTab;
        }) => `/databases/${databaseId}/projects/${projectId}/${tab}`,
        detail: ({
            databaseId,
            projectId,
            tab,
        }: {
            databaseId: string;
            projectId: string;
            tab: TDealsDetailTab;
        }) => `/databases/${databaseId}/projects/${projectId}/detail/${tab}`,
        activity: ({
            databaseId,
            projectId,
        }: {
            databaseId: string;
            projectId: string;
        }) => `/databases/${databaseId}/projects/${projectId}/activity`,
    },
    files: {
        upload: {
            path: ({
                projectId,
                databaseId,
            }: {
                projectId?: string | number;
                databaseId?: string | number;
            }) =>
                projectId !== undefined
                    ? `files/upload/database/${databaseId}/project/${projectId}`
                    : `files/upload`,
            url: ({
                projectId,
                databaseId,
            }: {
                projectId?: string | number;
                databaseId?: string | number;
            } = {}) =>
                projectId !== undefined
                    ? `files/upload/database/${databaseId}/project/${projectId}`
                    : "files/upload",
        },
        create: ({
            projectId,
            databaseId,
            fileId,
            tab,
        }: {
            projectId: string | number;
            databaseId: string | number;
            fileId: string | number;
            tab?: TDealsOverviewTab;
        }) =>
            tab !== undefined
                ? `databases/${databaseId}/projects/${projectId}/${tab}/files/${fileId}`
                : `files/${fileId}`,
    },
    dashboard: {
        read: (tab: TDashboardTab) => `/dashboard/${tab}`,
    },
    notFound: {
        default: () => "*",
    },
    thirdPartyDoc: {
        default: () => "/docs/",
    },
    openApiDoc: {
        default: () => "/docs/openapi",
    },
    userPolicies: {
        default: () => "/user-policies",
    },
    chartsOfAccounts: {
        dashboard: (tab: TChartsOfAccountsTab) => `/charts-of-accounts/${tab}`,
        view: (coaId: string) => `/charts-of-accounts/view/${coaId}`,
        editCoA: (coaId: string, tab: TChartOfAccountsEditTab) =>
            `/charts-of-accounts/edit/${coaId}/${tab}`,
        editMappings: (coaId: string) =>
            `/charts-of-accounts/edit/${coaId}/mappings`,
        auditLog: (coaId: string) => `/charts-of-accounts/audit-log/${coaId}`,
        upload: (coaId: string, tab: TChartOfAccountsEditTab) =>
            `/charts-of-accounts/edit/${coaId}/${tab}/upload`,
        create: () => `/charts-of-accounts/create`,
    },
};

// Legacy route builders (old URLs kept for fallback/redirects)
export const legacyRouterPaths = {
    deals: {
        read: (dealId: string, tab: TDealsOverviewTab) =>
            `/deals/${dealId}/${tab}`,
        detail: (dealId: string, tab: TDealsDetailTab) =>
            `/deals/${dealId}/detail/${tab}`,
        activity: (dealId: string) => `/deals/${dealId}/activity`,
        extraction: {
            mappings: (dealId: string, extractionId: string) =>
                `/deals/${dealId}/extraction/${extractionId}`,
            review: (dealId: string, extractionId: string) =>
                `/deals/${dealId}/extraction/${extractionId}/review`,
        },
    },
} as const;

// Matchers to detect legacy routes and extract params
export const legacyRouteMatchers = {
    dealsBase: /^\/deals\/(?<dealId>[^/]+)\/?(?<rest>.*)$/,
} as const;
